import { type ChangeEvent, type FC } from 'react';
import { type Control, useController } from 'react-hook-form';

import {
  type CompanyFeatureFlags,
  type CompanyForm,
  DEFAULT_COMPANY_FEATURE_FLAGS,
} from '@/types';
import { FormControlLabel, Grid, Switch, Typography } from '@/ui';

const FEATURE_METADATA: Record<
  string,
  { description?: string; label: string }
> = {
  documents: {
    description:
      'Controls access to uploading, managing, and sharing documents across the workspace.',
    label: 'Documents',
  },
  documentsUpload: {
    description:
      'Allow members to upload new files. Disabling keeps existing documents visible but blocks new uploads.',
    label: 'Document uploads',
  },
};

const FEATURE_KEYS = Object.keys(FEATURE_METADATA);

interface CompanyFeaturesProps {
  control: Control<CompanyForm>;
}

const CompanyFeatures: FC<CompanyFeaturesProps> = ({ control }) => {
  const { field } = useController({
    control,
    defaultValue: { ...DEFAULT_COMPANY_FEATURE_FLAGS },
    name: 'featureFlags',
  });

  const currentFlags = field.value as Partial<CompanyFeatureFlags> | undefined;

  const featureFlags: CompanyFeatureFlags = FEATURE_KEYS.reduce(
    (acc, feature) => {
      const currentValue = currentFlags?.[feature];

      if (typeof currentValue === 'boolean') {
        acc[feature] = currentValue;
        return acc;
      }

      const defaultValue = DEFAULT_COMPANY_FEATURE_FLAGS[feature];
      acc[feature] = typeof defaultValue === 'boolean' ? defaultValue : false;

      return acc;
    },
    {} as CompanyFeatureFlags,
  );

  const handleChange =
    (feature: string) => (event: ChangeEvent<HTMLInputElement>) => {
      field.onChange({
        ...featureFlags,
        [feature]: event.target.checked,
      });
    };

  return (
    <Grid spacing={4} container>
      <Grid md={4} xs={12} item>
        <Typography className="mb-6" variant="h4">
          Feature access
        </Typography>
        <Typography className="mb-6" variant="body1">
          Toggle access to high-level product areas for this company. Disabling
          a feature hides navigation and blocks direct URLs for members.
        </Typography>
      </Grid>
      <Grid className="flex flex-col gap-4" md={8} xs={12} item>
        {FEATURE_KEYS.map((feature) => {
          const enabled = featureFlags[feature];
          const metadata = FEATURE_METADATA[feature] ?? {
            label: feature,
          };
          return (
            <div key={feature} className="flex flex-col gap-1">
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled !== false}
                    color="primary"
                    onChange={handleChange(feature)}
                  />
                }
                label={metadata.label}
              />
              {metadata.description ? (
                <Typography
                  className="text-cement-700 ml-11 text-sm"
                  variant="body2"
                >
                  {metadata.description}
                </Typography>
              ) : null}
            </div>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default CompanyFeatures;
